import { BROWSER_EVENTS } from "@cms/utils/constants";
import { Breakpoint } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from "react";

type OpenDialogParams = {
    component: ReactNode;
    maxWidth?: Breakpoint;
};

type DialogContextType = {
    openDialog: ({ component, maxWidth }: OpenDialogParams) => void;
    closeDialog: () => void;
};

const DialogContext = createContext<DialogContextType | undefined>(undefined);

export const DialogProvider = ({ children }: { children: ReactNode }) => {
    const [dialogContent, setDialogContent] = useState<OpenDialogParams>({ component: null });

    const openDialog = useCallback(({ component, maxWidth }: OpenDialogParams) => setDialogContent({ component, maxWidth }), []);
    const closeDialog = useCallback(() => setDialogContent({ component: null }), []);

    useEffect(() => {
        const handler = () => {
            closeDialog();
        }

        window.addEventListener(BROWSER_EVENTS.CLOSE_MODAL, handler);

        return () => window.removeEventListener(BROWSER_EVENTS.CLOSE_MODAL, handler);
    }, []);

    return (
        <DialogContext.Provider value={{ openDialog, closeDialog }}>
            {children}

            <Dialog
                fullWidth
                disableRestoreFocus
                maxWidth={dialogContent.maxWidth || false}

                open={Boolean(dialogContent.component)}
                onClose={closeDialog}
            >
                {dialogContent.component}
            </Dialog>
        </DialogContext.Provider>
    );
};

export const useDialog = () => {
    const ctx = useContext(DialogContext);

    if (!ctx) throw new Error("useDialog must be used within a DialogProvider");

    return ctx;
};
